import { Building2, Users, BedDouble, LogIn, ArrowRight, Shield, BarChart3, LayoutDashboard, MapPin, Phone, Heart, Star, CheckCircle, MessageCircle, Quote, ChevronLeft, ChevronRight, TrendingUp, Clock, Award } from "lucide-react";
import heroBgImg from "@/assets/landing/hero-bg.webp";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState, useCallback } from "react";
import { getClinicasFull } from "@/lib/clinicasCache";
import LandingFAQ from "@/components/landing/LandingFAQ";
import LandingProcess from "@/components/landing/LandingProcess";
import LandingDifferentials from "@/components/landing/LandingDifferentials";
import LandingBlog from "@/components/landing/LandingBlog";
import LandingServices from "@/components/landing/LandingServices";
import LandingLocationSelector from "@/components/landing/LandingLocationSelector";
import LandingUrgencyCTA from "@/components/landing/LandingUrgencyCTA";
import { useSiteSetting } from "@/hooks/useSiteSetting";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const features = [
  { icon: Building2, label: "Clínicas Verificadas", desc: "Centenas de clínicas verificadas em todo o Brasil", color: "text-primary" },
  { icon: MapPin, label: "Busca Inteligente", desc: "Encontre clínicas próximas por mapa interativo", color: "text-accent" },
  { icon: BedDouble, label: "Todos os Tipos", desc: "Internação voluntária, involuntária e compulsória", color: "text-primary" },
  { icon: Star, label: "Compare e Escolha", desc: "Preços, estrutura e avaliações reais", color: "text-accent" },
];

const benefits = [
  "Equipe multidisciplinar com médicos, psicólogos e terapeutas",
  "Atendimento 24 horas para emergências e orientação",
  "Tratamento personalizado para dependência química e alcoolismo",
  "Internação voluntária, involuntária e compulsória",
  "Acompanhamento familiar durante todo o processo",
  "Ambiente seguro e acolhedor para recuperação",
];

const testimonials = [
  {
    name: "Maria S.",
    role: "Mãe de paciente recuperado",
    text: "Graças ao Busca Clínicas encontramos a clínica perfeita para meu filho. Hoje ele está sóbrio há 2 anos. A plataforma facilitou muito a busca e comparação entre as clínicas.",
    rating: 5,
    city: "São Paulo, SP",
  },
  {
    name: "Carlos R.",
    role: "Paciente recuperado",
    text: "Depois de anos lutando contra a dependência, encontrei através desta plataforma uma clínica que mudou minha vida. O tratamento foi humanizado e a equipe excepcional.",
    rating: 5,
    city: "Belo Horizonte, MG",
  },
  {
    name: "Ana P.",
    role: "Esposa de paciente",
    text: "A facilidade de comparar clínicas, ver avaliações e entrar em contato direto pelo WhatsApp foi essencial. Meu marido está em recuperação e evoluindo a cada dia.",
    rating: 5,
    city: "Curitiba, PR",
  },
  {
    name: "Roberto L.",
    role: "Paciente recuperado",
    text: "Internei voluntariamente e foi a melhor decisão da minha vida. O Busca Clínicas me ajudou a encontrar uma clínica com o tratamento certo para o meu caso.",
    rating: 5,
    city: "Rio de Janeiro, RJ",
  },
  {
    name: "Fernanda M.",
    role: "Filha de paciente",
    text: "Minha mãe sofria com alcoolismo há anos. Pelo Busca Clínicas encontramos uma clínica feminina especializada. Hoje ela está recuperada e feliz.",
    rating: 5,
    city: "Salvador, BA",
  },
];

const stats = [
  { icon: Building2, value: "500+", label: "Clínicas Cadastradas" },
  { icon: Users, value: "10.000+", label: "Famílias Atendidas" },
  { icon: Award, value: "98%", label: "Satisfação" },
  { icon: Clock, value: "24h", label: "Atendimento" },
];

interface EstadoInfo { estado_slug: string; estado: string; estado_nome: string; count: number }

const Index = () => {
  const navigate = useNavigate();
  const { user, isReady } = useAuth();
  const [estados, setEstados] = useState<EstadoInfo[]>([]);
  const [totalClinicas, setTotalClinicas] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const whatsappPhone = useSiteSetting("whatsapp_phone", "5562994377238");

  useDocumentMeta({
    title: "Busca Clínicas - Clínicas de Reabilitação e Recuperação no Brasil",
    description: "Encontre as melhores clínicas de reabilitação e recuperação no Brasil. Compare preços, avaliações e tratamentos para dependência química e alcoolismo. Atendimento 24h.",
    canonical: "https://buscaclinicas.com.br/",
    ogType: "website",
  });
  useEffect(() => {
    const fetchData = async () => {
      const allClinicas = await getClinicasFull();
      const withSlug = allClinicas.filter((c: any) => c.slug && c.estado_slug);
      setTotalClinicas(withSlug.length);

      const map = new Map<string, EstadoInfo>();
      withSlug.forEach((c: any) => {
        if (!map.has(c.estado)) {
          map.set(c.estado, {
            estado: c.estado,
            estado_slug: c.estado_slug,
            estado_nome: c.estado, // estado field already has full name
            count: 0,
          });
        }
        map.get(c.estado)!.count++;
      });
      setEstados(Array.from(map.values()).sort((a, b) => b.count - a.count).slice(0, 12));
    };
    fetchData();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const prevTestimonial = useCallback(() => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, []);

  const nextTestimonial = useCallback(() => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  }, []);

  return (
    <main className="min-h-screen bg-background">
      {/* Hero Section */}
      <header className="relative overflow-hidden">
        {/* Hero background image */}
        <div className="absolute inset-0">
         <img
            src={heroBgImg}
            alt="Luta contra a dependência - encontre ajuda profissional"
            className="w-full h-full object-cover hidden sm:block"
            fetchPriority="high"
            decoding="async"
            width={1280}
            height={720}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/70 dark:from-background dark:via-background/95 dark:to-background/80" />
        </div>
        <div className="pointer-events-none absolute inset-0 sm:hidden">
          <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 h-[400px] w-[400px] rounded-full bg-accent/5 blur-3xl" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 py-16 sm:py-24">
          <div className="max-w-2xl space-y-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/25">
              <Building2 className="h-8 w-8" />
            </div>
            <h1 className="text-3xl sm:text-5xl font-bold tracking-tight text-foreground">
              Clínicas de Reabilitação e <span className="text-primary">Recuperação</span> no Brasil
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Encontre as melhores clínicas de reabilitação para tratamento de dependência química, alcoolismo e saúde mental.
              Compare preços, avaliações e estrutura. Atendimento 24 horas.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button size="lg" className="h-12 text-base font-semibold gap-2 shadow-md shadow-primary/20" asChild>
                <a
                  href={`https://wa.me/${whatsappPhone}?text=Olá! Preciso de ajuda para encontrar uma clínica de reabilitação.`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MessageCircle className="h-5 w-5" /> Peça Ajuda Agora
                  <ArrowRight className="h-4 w-4 ml-auto" />
                </a>
              </Button>
              <Button size="lg" variant="outline" className="h-12 text-base gap-2" asChild>
                <Link to="/mapa">
                  <MapPin className="h-5 w-5" /> Encontrar Clínicas
                </Link>
              </Button>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground pt-2">
              <span className="flex items-center gap-1"><Shield className="h-4 w-4" /> Seguro</span>
              <span className="flex items-center gap-1"><CheckCircle className="h-4 w-4" /> {totalClinicas}+ clínicas</span>
              <span className="flex items-center gap-1"><Phone className="h-4 w-4" /> 24h</span>
            </div>
          </div>
        </div>
      </header>

      {/* Stats bar */}
      <section className="border-y bg-muted/20">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((s) => (
              <div key={s.label} className="flex flex-col items-center text-center gap-2">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <s.icon className="h-5 w-5 text-primary" />
                </div>
                <span className="text-2xl font-bold text-foreground">{s.value}</span>
                <span className="text-xs text-muted-foreground">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-5xl mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold text-foreground text-center mb-2">Por que escolher o Busca Clínicas?</h2>
        <p className="text-muted-foreground text-center text-sm mb-8">A plataforma mais completa para encontrar clínicas de reabilitação</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((f) => (
            <Card key={f.label} className="border-border/50 bg-card/80 backdrop-blur-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
              <CardContent className="p-5 flex flex-col items-center text-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                  <f.icon className={`h-6 w-6 ${f.color}`} />
                </div>
                <span className="text-sm font-semibold text-foreground">{f.label}</span>
                <span className="text-xs text-muted-foreground leading-relaxed">{f.desc}</span>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* How it works */}
      <LandingProcess />

      {/* Services Cards */}
      <LandingServices />

      {/* Urgency CTA */}
      <LandingUrgencyCTA />

      {/* Testimonials */}
      <section className="bg-muted/30 border-y">
        <div className="max-w-5xl mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold text-foreground text-center mb-2">Histórias de Recuperação</h2>
          <p className="text-muted-foreground text-center text-sm mb-8">
            Veja como ajudamos milhares de famílias a encontrar o caminho da recuperação
          </p>

          <div className="relative max-w-3xl mx-auto">
            <button
              onClick={prevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 sm:-translate-x-6 z-10 w-10 h-10 rounded-full bg-card border border-border shadow-md flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Depoimento anterior"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={nextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 sm:translate-x-6 z-10 w-10 h-10 rounded-full bg-card border border-border shadow-md flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Próximo depoimento"
            >
              <ChevronRight className="h-5 w-5" />
            </button>

            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentTestimonial * 100}%)` }}
              >
                {testimonials.map((t, i) => (
                  <div key={i} className="w-full flex-shrink-0 px-4">
                    <Card className="border-border/50 bg-card shadow-lg">
                      <CardContent className="p-6 sm:p-8">
                        <div className="flex items-center gap-1 mb-4">
                          {Array.from({ length: t.rating }).map((_, j) => (
                            <Star key={j} className="h-4 w-4 fill-primary text-primary" />
                          ))}
                        </div>
                        <Quote className="h-8 w-8 text-primary/20 mb-3" />
                        <p className="text-foreground leading-relaxed mb-6 text-sm sm:text-base italic">
                          "{t.text}"
                        </p>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-primary font-bold text-sm">{t.name.charAt(0)}</span>
                          </div>
                          <div>
                            <p className="font-semibold text-sm text-foreground">{t.name}</p>
                            <p className="text-xs text-muted-foreground">{t.role} — {t.city}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-center gap-3 mt-6">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentTestimonial(i)}
                  className={`h-8 min-w-[2rem] flex items-center justify-center rounded-full transition-all duration-300 ${
                    i === currentTestimonial ? "bg-primary w-8" : "bg-muted-foreground/30 hover:bg-muted-foreground/50 w-8"
                  }`}
                  aria-label={`Depoimento ${i + 1}`}
                >
                  <span className={`block rounded-full transition-all ${i === currentTestimonial ? "w-5 h-2.5 bg-primary-foreground" : "w-2.5 h-2.5 bg-foreground/50"}`} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Differentials */}
      <LandingDifferentials />

      {/* About / SEO content */}
      <section className="max-w-5xl mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto space-y-6">
          <h2 className="text-2xl font-bold text-foreground text-center">Tratamento Especializado em Dependência Química</h2>
          <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              O Busca Clínicas é a plataforma mais completa para encontrar clínicas de reabilitação e recuperação em todo o Brasil.
              Nosso objetivo é facilitar a busca por tratamento especializado em dependência química, alcoolismo, transtornos psiquiátricos
              e outras condições que necessitam de internação e acompanhamento profissional.
            </p>
            <p>
              Sabemos que a escolha de uma clínica de reabilitação é uma decisão importante e delicada para pacientes e familiares.
              Por isso, reunimos informações detalhadas sobre centenas de clínicas cadastradas, incluindo valores, tipos de tratamento,
              estrutura física, equipe médica e avaliações de outros pacientes.
            </p>
            <p>
              Todas as clínicas listadas oferecem tratamento com equipe multidisciplinar, incluindo médicos psiquiatras, psicólogos,
              terapeutas ocupacionais, assistentes sociais e enfermeiros especializados.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 pt-2">
            {benefits.map((b, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <span className="text-muted-foreground">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Selector */}
      <LandingLocationSelector />

      {/* States listing */}
      {estados.length > 0 && (
        <section className="bg-muted/20">
          <div className="max-w-5xl mx-auto px-4 py-12">
            <h2 className="text-2xl font-bold text-foreground text-center mb-2">Clínicas de Reabilitação por Estado</h2>
            <p className="text-muted-foreground text-center text-sm mb-8">Encontre clínicas de recuperação na sua região</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {estados.map((e) => (
                <Link
                  key={e.estado}
                  to={`/clinicas/${e.estado_slug}`}
                  className="flex items-center justify-between p-3 rounded-lg border border-border/50 hover:border-primary/50 hover:shadow-sm hover:-translate-y-0.5 transition-all bg-card"
                >
                  <span className="text-sm font-medium text-foreground">{e.estado_nome}</span>
                  <span className="text-xs text-muted-foreground">{e.count} clínicas</span>
                </Link>
              ))}
            </div>
            <div className="text-center mt-6">
              <Button variant="outline" asChild>
                <Link to="/mapa">
                  <MapPin className="h-4 w-4 mr-2" /> Ver todas no mapa
                </Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Blog */}
      <LandingBlog />

      {/* FAQ */}
      <LandingFAQ />

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-primary/5" />
        <div className="relative max-w-3xl mx-auto px-4 py-16 text-center space-y-4">
          <Heart className="h-10 w-10 text-primary mx-auto" />
          <h2 className="text-2xl font-bold text-foreground">Precisa de ajuda para encontrar uma clínica?</h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Nossa equipe está disponível 24 horas para ajudar você a encontrar a melhor clínica de reabilitação
            para o seu caso. Fale conosco pelo WhatsApp e receba orientação gratuita.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            <Button size="lg" className="gap-2 shadow-md shadow-primary/20" asChild>
              <a href={`https://wa.me/${whatsappPhone}?text=Olá, preciso de ajuda para encontrar uma clínica de reabilitação.`} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="h-5 w-5" /> Falar com um Atendente
              </a>
            </Button>
            <Button size="lg" variant="outline" className="gap-2" asChild>
              <Link to="/mapa">
                <MapPin className="h-5 w-5" /> Explorar o Mapa
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalBusiness",
            "name": "Busca Clínicas",
            "url": "https://buscaclinicas.com.br",
            "description": "Plataforma para encontrar clínicas de reabilitação e recuperação no Brasil. Compare preços, avaliações e tratamentos para dependência química e alcoolismo.",
            "areaServed": { "@type": "Country", "name": "Brasil" },
            "serviceType": ["Clínica de Reabilitação", "Tratamento de Dependência Química", "Internação Voluntária", "Internação Involuntária"],
            "openingHoursSpecification": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
              "opens": "00:00",
              "closes": "23:59",
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "10000",
              "bestRating": "5",
            },
          }),
        }}
      />
    </main>
  );
};

export default Index;
